import { useEffect } from 'react';
import classNames from 'classnames/bind';
import styles from '@/styles/superstack/companies/CardCompanyListRenewal.module.scss';
import SSImage from '@/src/component/superstack/base/SSImage';
import CompanyLogoDefault from '@/asset/superstack/svgs/company-logo-default.svg';
import useRecommendCompanyCultures from '@/src/utils/hooks/superstack/useRecommendCompanyCultures';
import LinkTag from '@/src/component/LinkTag';
import { CultureLinks } from '@/data/superstack/CultureLinks';
import ImgCompanyCultureListBlur from '@/asset/superstack/pngs/img-company-culture-list-blur.png';
import ImgCompanyCultureListBlurPC from '@/asset/superstack/svgs/img-company-culture-list-blur-pc.svg';
import useCompanyCultures from '@/src/utils/hooks/superstack/useCompanyCultures';
import FadeLoader from 'react-spinners/FadeLoader';
import { SSPagination } from '@/src/component/superstack/base/Pagination';
import useSSProfile from '@/src/utils/hooks/superstack/useSSProfile';
import { CardBannerOnBlur } from '@/src/component/superstack/companies/CardBannerOnBlur';
import { useRecoilValue } from 'recoil';
import { mobileState } from '@/src/utils/hooks/useMobileSize';
import { CompanyMatchingScoreLayer } from '@/src/component/superstack/base/MatchingScoreLayer';

const cx = classNames.bind(styles);

const CardCompanyCultureList = ({
  n,
  page,
  orderBy,
  keyword,
  handlePageChange,
  paginatedBy,
  ...props
}) => {
  const { user: { data: { username } = {} } = {} } = useSSProfile();
  const { data: { data: { companies = [], total_cnt } = {} } = {}, isLoading } = useCompanyCultures(
    {
      n,
      page,
      orderBy,
      keyword,
      username,
    },
  );

  useEffect(() => {
    if (total_cnt >= 0) {
      const maxPage = total_cnt === 0 ? 1 : Math.floor((total_cnt - 1) / paginatedBy) + 1;
      if (page > maxPage) {
        handlePageChange(null, maxPage);
      }
    }
  }, [page, total_cnt, paginatedBy]);

  const cx = classNames.bind(styles);
  const displayingCompanies = n ? companies.slice(0, n) : companies;
  return (
    <>
      <section className={cx('card-company-list-renewal')}>
        <FadeLoader
          color={'gray'}
          loading={isLoading}
          cssOverride={{
            margin: '200px auto',
          }}
        />
        {!isLoading && total_cnt === 0 && (
          <article className={cx('empty-result-box')}>
            <p className={cx('title')}>결과가 없습니다.</p>
            <ul style={{ listStyleType: 'disc', marginTop: '20px' }}>
              <li>필터를 다시 설정하거나, 필터 개수를 줄여보세요.</li>
              <li>단어의 철자가 정확한지 확인해 보세요.</li>
              <li>한글을 영어로 혹은 영어를 한글로 입력했는지 확인해 보세요.</li>
              <li>검색어의 단어 수를 줄이거나, 보다 일반적인 검색어로 다시 검색해 보세요.</li>
              <li>두 단어 이상의 검색어인 경우, 띄어쓰기를 확인해 보세요.</li>
            </ul>
          </article>
        )}
        {displayingCompanies.map((company) => {
          return <CardCompanyCulture key={company.id} company={company} {...props} />;
        })}
      </section>
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <SSPagination
          countItems={total_cnt}
          paginatiedBy={paginatedBy}
          page={page}
          handlePageChange={handlePageChange}
        />
      </div>
    </>
  );
};

const CardCompanyCultureRecList = ({ n = null, page, ...props }) => {
  const { user: { data: { username } = {} } = {} } = useSSProfile();
  const {
    data: { data: { companies = [] } = {} } = {},
    isLoading,
    isError,
  } = useRecommendCompanyCultures(username, n, page);

  const cx = classNames.bind(styles);
  const displayingCompanies = n ? companies.slice(0, n) : companies;
  const isMobile = useRecoilValue(mobileState);
  return (
    <>
      <section className={cx('card-company-list-renewal')}>
        <FadeLoader
          color={'gray'}
          loading={isLoading}
          cssOverride={{
            margin: '200px auto',
          }}
        />
        {displayingCompanies.map((company) => {
          return (
            <CardCompanyCulture key={company.id} company={company} nInRow={n || 2} {...props} />
          );
        })}
        {!isLoading && (isError || !displayingCompanies.length) && (
          <section style={{ textAlign: 'center', width: '100%', position: 'relative' }}>
            {isMobile ? (
              <SSImage layout={'responsive'} src={ImgCompanyCultureListBlur} />
            ) : (
              <ImgCompanyCultureListBlurPC />
            )}
            <CardBannerOnBlur
              text1={'나와 컬쳐 코드가 잘 맞는'}
              text2={'기업이 궁금하다면?'}
              btnName={'선호 컬쳐 코드 작성하기'}
              btnUrl={
                CultureLinks().find((url) => url.name === 'user_prefer_culture_self_router').url
              }
              style={
                isMobile
                  ? {}
                  : {
                      maxWidth: '352px',
                      top: '0',
                      right: '30%',
                      transform: 'none',
                      left: 'auto',
                    }
              }
            />
          </section>
        )}
      </section>
    </>
  );
};

const CardCompanyCulture = ({ company, nInRow }) => {
  const logoImg = company?.logo ? (
    <SSImage src={company.logo} width={48} height={48} objectFit={'contain'} />
  ) : (
    <CompanyLogoDefault width={48} height={48} />
  );
  return (
    <article className={cx(['card', nInRow === 3 ? 'three-in-row' : ''])}>
      <LinkTag
        href={
          CultureLinks(company.id).find((url) => url.name === 'company_culture_review_result').url
        }
        isNextPage={true}
      >
        <div className={cx('head')}>
          <div className={cx('name-wrapper')}>
            <p className={cx('name')}>{company.name}</p>
            {company.headcount > 0 && (
              <p className={cx('subtitle')}>총 {company.headcount}명 재직중</p>
            )}
          </div>
          <div className={cx('logo-wrapper')}>{logoImg}</div>
        </div>
        <CompanyMatchingScoreLayer
          companyId={company.id}
          stacksOff={true}
          style={{ marginTop: '12px' }}
        />
      </LinkTag>
    </article>
  );
};

export { CardCompanyCultureRecList, CardCompanyCultureList };
